<script setup>
/* global defineProps, defineEmits */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { cancelAllRequests, getActiveRequestCount, profileService } from '@/services/api'
import RoleSwitcher from '@/components/RoleSwitcher.vue'

import globeIcon from '@/assets/globe.svg'
import logoImage from '@/assets/Logo4.svg'

const props = defineProps({
  showBackButton: { type: Boolean, default: false },
  backRoute: { type: [String, Object], default: null },
  backButtonTitle: { type: String, default: '' },
  customBackFunction: { type: Function, default: null },
  isDark: { type: Boolean, default: true },
  selectedLanguage: { type: String, default: '' },
})

const emit = defineEmits([
  'back',
  'update:isDark',
  'update:selectedLanguage',
  'theme-change',
  'language-change',
])

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const isLanguageOpen = ref(false)
const isUserMenuOpen = ref(false)
const isMobileMenuOpen = ref(false)
const showProfileModal = ref(false)
const updatingProfile = ref(false)
const selectedDb = ref(authStore.activeDatabase || 'db1')
const isMounted = ref(true)

const currentLang = ref(props.selectedLanguage || localStorage.getItem('landing-language') || localStorage.getItem('lang') || locale.value || 'fa')
const profileData = ref({
  customer_id: '',
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
})
const originalProfileData = ref({})

const isDarkModel = computed({
  get: () => props.isDark,
  set: (value) => emit('update:isDark', value),
})

const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)
//const selectedCompany = computed(() => authStore.selectedCompany)
//const companyDatabases = computed(() => authStore.companyDatabases || [])
const activeDatabase = computed(() => authStore.activeDatabase)
//const switchingDatabase = computed(() => authStore.switchingDatabase)

const userFirstName = computed(() => (
  user.value?.first_name || user.value?.name || user.value?.phone || 'User'
))

const userInitial = computed(() => (
  user.value?.first_name?.charAt(0)?.toUpperCase()
  || user.value?.name?.charAt(0)?.toUpperCase()
  || user.value?.phone?.charAt(0)
  || 'U'
))

const hasChanges = computed(() => (
  JSON.stringify(profileData.value) !== JSON.stringify(originalProfileData.value)
))

const showRoleSwitcher = computed(() => (
  route.path !== '/'
  && !route.path.startsWith('/company/')
  && authStore.isAuthenticated
  && authStore.hasMultipleRoles
))

const languages = [
  { value: 'fa', label: 'فارسی', flag: '🇮🇷' },
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'ar', label: 'العربية', flag: '🇸🇦' },
]

const activeLanguage = computed(() => (
  languages.find((language) => language.value === currentLang.value) || languages[0]
))

const navItems = computed(() => [
  { id: 'about', label: t('landing.footer.quick.about') },
  { id: 'modules', label: t('landing.erp.modules.title') },
  { id: 'ai', label: t('landing.erp.ai.title') },
  { id: 'contact', label: t('landing.footer.quick.contact') },
])

const scrollToSection = (id) => {
  isUserMenuOpen.value = false
  isMobileMenuOpen.value = false
  const target = document.getElementById(id)
  if (!target) return

  const top = target.getBoundingClientRect().top + window.scrollY - 24
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const cancelRequests = () => {
  if (getActiveRequestCount() > 0) cancelAllRequests()
}

const navigateTo = (path) => {
  cancelRequests()
  window.setTimeout(() => router.push(path), 50)
}

const goBack = async () => {
  cancelRequests()
  if (props.customBackFunction) {
    await props.customBackFunction()
  } else if (props.backRoute) {
    navigateTo(props.backRoute)
  } else {
    emit('back')
  }
}

const selectLanguage = (value) => {
  currentLang.value = value
  locale.value = value
  isLanguageOpen.value = false
  document.documentElement.dir = value === 'en' ? 'ltr' : 'rtl'
  localStorage.setItem('lang', value)
  localStorage.setItem('landing-language', value)
  emit('update:selectedLanguage', value)
  emit('language-change', value)
}

const toggleTheme = () => {
  const nextIsDark = !isDarkModel.value
  isDarkModel.value = nextIsDark
  const theme = nextIsDark ? 'dark' : 'light'
  document.documentElement.setAttribute('data-theme', theme)
  localStorage.setItem('theme', theme)
  localStorage.setItem('landing-theme', theme)
  emit('theme-change', theme)
}

const openProfileModal = async () => {
  closeMobileMenu()
  try {
    const response = await profileService.getCustomerProfile()
    const customerData = response.data.customer_data

    profileData.value = {
      customer_id: customerData.id,
      first_name: customerData.first_name || '',
      last_name: customerData.last_name || '',
      email: customerData.email || '',
      phone_number: customerData.phone_number || '',
    }
    originalProfileData.value = { ...profileData.value }
    showProfileModal.value = true
  } catch (error) {
    if (error?.cancelled) return

    if (user.value) {
      profileData.value = {
        customer_id: user.value.id || user.value.customer_id,
        first_name: user.value.first_name || '',
        last_name: user.value.last_name || '',
        email: user.value.email || '',
        phone_number: user.value.phone || user.value.phone_number || '',
      }
      originalProfileData.value = { ...profileData.value }
      showProfileModal.value = true
    } else {
      window.alert(t('navbar.profile.loadError', 'Unable to load profile.'))
    }
  }
}

const closeProfileModal = () => {
  showProfileModal.value = false
  profileData.value = { ...originalProfileData.value }
}

const updateProfile = async () => {
  if (!profileData.value.customer_id || !hasChanges.value) return

  updatingProfile.value = true
  try {
    const updateData = { customer_id: Number(profileData.value.customer_id) }
    const fields = ['first_name', 'last_name', 'email']

    fields.forEach((field) => {
      if (profileData.value[field] !== originalProfileData.value[field]) {
        updateData[field] = profileData.value[field] || ''
      }
    })

    if (Object.keys(updateData).length === 1) return

    const response = await profileService.updateProfile(updateData)
    if (!response.data.success) throw new Error(response.data.message || 'Profile update failed')

    if (authStore.user && response.data.customer) {
      const customer = response.data.customer
      authStore.user.first_name = customer.first_name
      authStore.user.last_name = customer.last_name
      authStore.user.email = customer.email
      authStore.user.name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || authStore.user.phone
      localStorage.setItem('user', JSON.stringify(authStore.user))
    }

    originalProfileData.value = { ...profileData.value }
    window.alert(t('navbar.profile.updated', 'Profile updated successfully.'))
    showProfileModal.value = false
  } catch (error) {
    profileData.value = { ...originalProfileData.value }
    window.alert(error?.response?.data?.detail || error?.message || t('navbar.profile.updateError', 'Unable to update profile.'))
  } finally {
    updatingProfile.value = false
  }
}

// const handleDatabaseChange = async () => {
//   const oldDatabase = authStore.activeDatabase
//   try {
//     await authStore.switchDatabase(selectedDb.value)
//     window.location.reload()
//   } catch (error) {
//     selectedDb.value = oldDatabase || 'db1'
//   }
// }

const handleLogout = async () => {
  try {
    cancelRequests()
    await authStore.logout()
    window.location.href = '/'
  } catch (error) {
    console.error('Logout error:', error)
  }
}

const handleOutsideClick = (event) => {
  if (!event.target.closest('.language-picker')) isLanguageOpen.value = false
  if (!event.target.closest('.user-menu')) isUserMenuOpen.value = false
  if (!event.target.closest('.mobile-menu') && !event.target.closest('.mobile-menu-toggle')) closeMobileMenu()
  if (showProfileModal.value && event.target.classList.contains('profile-modal-overlay')) closeProfileModal()
}

const handleBeforeUnload = () => cancelRequests()

watch(activeDatabase, (value) => {
  selectedDb.value = value || 'db1'
})

watch(() => props.selectedLanguage, (value) => {
  if (value && value !== currentLang.value) selectLanguage(value)
})

onMounted(async () => {
  const savedTheme = localStorage.getItem('landing-theme') || localStorage.getItem('theme')
  const savedLanguage = props.selectedLanguage || localStorage.getItem('landing-language') || localStorage.getItem('lang')

  if (savedTheme) emit('update:isDark', savedTheme === 'dark')
  if (savedLanguage && languages.some((language) => language.value === savedLanguage)) selectLanguage(savedLanguage)
  else selectLanguage(currentLang.value)

  window.addEventListener('beforeunload', handleBeforeUnload)
  document.addEventListener('click', handleOutsideClick)

  if (authStore.token) {
    await authStore.checkAuth()
    if (authStore.selectedCompany) await authStore.fetchCompanyDatabases()
  }
})

onBeforeUnmount(() => {
  isMounted.value = false
  window.removeEventListener('beforeunload', handleBeforeUnload)
  document.removeEventListener('click', handleOutsideClick)
})

onBeforeRouteLeave((to, from, next) => {
  cancelRequests()
  window.setTimeout(() => next(), 50)
})
</script>

<template>
  <div class="navbar-styled" :class="{ 'language-english': currentLang === 'en' }">
    <nav class="landing-nav" dir="rtl" aria-label="Primary navigation">
      <button
        class="mobile-menu-toggle"
        type="button"
        :aria-expanded="isMobileMenuOpen"
        aria-controls="mobile-navigation-drawer"
        :aria-label="isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'"
        @click.stop="isMobileMenuOpen = !isMobileMenuOpen"
      >
        <span class="menu-icon" aria-hidden="true"><i></i><i></i><i></i></span>
      </button>

      <button
        v-if="showBackButton"
        class="nav-icon-button back-button"
        type="button"
        :title="backButtonTitle || t('back')"
        :aria-label="backButtonTitle || t('back')"
        @click="goBack"
      >
        <span aria-hidden="true">{{ currentLang === 'en' ? '←' : '→' }}</span>
      </button>

      <div class="brand-cluster">
        <a class="nav-brand" href="/" :aria-label="t('brand')" @click.prevent="scrollToSection('top')">
          <img @click="navigateTo('/')" :src="logoImage" :alt="t('brand')" />
        </a>
        <p @click="navigateTo('/')" class="almas-text">{{t('brand')}}</p>
        <div v-if="showRoleSwitcher" class="role-switcher-wrap">
          <RoleSwitcher />
        </div>

        <div v-if="isAuthenticated" class="user-menu">
          <button
            class="user-trigger"
            type="button"
            :aria-expanded="isUserMenuOpen"
            @click.stop="isUserMenuOpen = !isUserMenuOpen"
          >
            <span class="user-avatar">{{ userInitial }}</span>
            <span class="user-name">{{ userFirstName }}</span>
            <span class="user-chevron" aria-hidden="true">⌄</span>
          </button>

          <div v-if="isUserMenuOpen" class="user-dropdown" role="menu">
            <strong>{{ userFirstName }}</strong>
            <span class="dropdown-divider"></span>
            <button type="button" role="menuitem" @click="openProfileModal(); isUserMenuOpen = false">
              <span aria-hidden="true">◉</span>{{ t('profile') }}
            </button>
            <button type="button" role="menuitem" class="danger-action" @click="handleLogout">
              <span aria-hidden="true">↪</span>{{ t('logout') }}
            </button>
          </div>
        </div>

        <div v-if="!isAuthenticated" class="auth-actions" aria-label="Account actions">
          <a class="auth-link auth-login" href="/login">{{ t('login') }}</a>
          <a class="auth-link auth-register" href="/register">{{ t('signup') }}</a>
        </div>
      </div>

      <div class="nav-links">
        <a v-for="item in navItems" :key="item.id" :href="`#${item.id}`" @click.prevent="scrollToSection(item.id)">
          {{ item.label }}
        </a>
      </div>

      <div class="nav-controls" aria-label="Site preferences">
        <div class="language-picker" :class="{ 'is-open': isLanguageOpen }">
          <button
            class="language-trigger"
            type="button"
            aria-haspopup="listbox"
            :aria-expanded="isLanguageOpen"
            @click.stop="isLanguageOpen = !isLanguageOpen"
          >
            <span class="language-flag">{{ activeLanguage.flag }}</span>
            <span class="language-name">{{ activeLanguage.label }}</span>
            <img class="language-chevron" :src="globeIcon" alt="" aria-hidden="true" />
          </button>

          <div v-if="isLanguageOpen" class="language-menu" role="listbox">
            <button
              v-for="language in languages"
              :key="language.value"
              class="language-option"
              type="button"
              role="option"
              :aria-selected="currentLang === language.value"
              @click="selectLanguage(language.value)"
            >
              <span class="language-flag">{{ language.flag }}</span>
              <span>{{ language.label }}</span>
            </button>
          </div>
        </div>

        <button
          class="theme-toggle"
          type="button"
          :aria-pressed="isDarkModel"
          :aria-label="isDarkModel ? 'Switch to light mode' : 'Switch to dark mode'"
          @click="toggleTheme"
        >
          <span aria-hidden="true">{{ isDarkModel ? '☼' : '☾' }}</span>
        </button>
      </div>
    </nav>

    <div v-if="isMobileMenuOpen" class="mobile-menu-backdrop" aria-hidden="true" @click="closeMobileMenu"></div>

    <aside
      id="mobile-navigation-drawer"
      class="mobile-menu"
      :class="{ 'is-open': isMobileMenuOpen }"
      :dir="currentLang === 'en' ? 'ltr' : 'rtl'"
      aria-label="Mobile navigation"
    >
      <div class="mobile-menu-header">
        <img :src="logoImage" :alt="t('brand')" />
        <button class="mobile-menu-close" type="button" aria-label="Close navigation menu" @click="closeMobileMenu">×</button>
      </div>

      <div v-if="isAuthenticated" class="mobile-user-card">
        <span class="user-avatar">{{ userInitial }}</span>
        <div>
          <strong>{{ userFirstName }}</strong>
          <small>{{ t('profile') }}</small>
        </div>
      </div>

      <nav class="mobile-menu-links">
        <a v-for="item in navItems" :key="item.id" :href="`#${item.id}`" @click.prevent="scrollToSection(item.id)">
          {{ item.label }}
        </a>

        <template v-if="!isAuthenticated">
          <a href="/login" @click="closeMobileMenu">{{ t('login') }}</a>
          <a class="mobile-register-link" href="/register" @click="closeMobileMenu">{{ t('signup') }}</a>
        </template>

        <template v-else>
          <button type="button" @click="openProfileModal">{{ t('profile') }}</button>
          <button type="button" class="mobile-danger-link" @click="handleLogout">{{ t('logout') }}</button>
        </template>
      </nav>

      <div v-if="showRoleSwitcher" class="mobile-role-switcher">
        <RoleSwitcher />
      </div>
    </aside>

    <!-- <div v-if="isAuthenticated && selectedCompany" class="company-bar">
      <div class="company-name"><span aria-hidden="true">▣</span>{{ selectedCompany.company_name }}</div>
      <div v-if="companyDatabases.length > 1" class="database-switcher">
        <select v-model="selectedDb" :disabled="switchingDatabase" @change="handleDatabaseChange">
          <option v-for="database in companyDatabases" :key="database.key" :value="database.key">
            {{ database.label || database.name }}
          </option>
        </select>
      </div>
      <span v-else-if="companyDatabases.length === 1" class="active-database">
        {{ companyDatabases[0].label || companyDatabases[0].name }}
      </span>
    </div> -->

    <div v-if="showProfileModal" class="profile-modal-overlay" @click.self="closeProfileModal">
      <section class="profile-modal" role="dialog" aria-modal="true" :aria-label="t('navbar.profile.title')">
        <header class="profile-modal-header">
          <h2>{{ t('navbar.profile.title') }}</h2>
          <button class="modal-close" type="button" @click="closeProfileModal">×</button>
        </header>

        <div class="profile-avatar">{{ userInitial }}</div>

        <label>
          <span>{{ t('navbar.profile.firstName') }}</span>
          <input v-model="profileData.first_name" type="text" />
        </label>
        <label>
          <span>{{ t('navbar.profile.lastName') }}</span>
          <input v-model="profileData.last_name" type="text" />
        </label>
        <label>
          <span>{{ t('navbar.profile.email') }}</span>
          <input v-model="profileData.email" type="email" />
        </label>
        <label>
          <span>{{ t('navbar.profile.phone') }}</span>
          <input v-model="profileData.phone_number" type="tel" disabled />
        </label>

        <footer class="profile-modal-actions">
          <button class="button button-ghost" type="button" @click="closeProfileModal">{{ t('navbar.profile.cancel') }}</button>
          <button class="button button-primary" type="button" :disabled="!hasChanges || updatingProfile" @click="updateProfile">
            {{ updatingProfile ? '…' : t('navbar.profile.save') }}
          </button>
        </footer>
      </section>
    </div>
  </div>
</template>

<style scoped>
.almas-text 
{
  margin: 0px;
  font-weight: 900;
  display: flex;
  align-self: center;
  color: #81cbff;
  text-shadow: 1cm;
  justify-content: center;
  
}
@media (max-width: 760px) {

  .almas-text {
    min-width: 0;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }  
}
@media (max-width: 420px) {
.almas-text {
    max-width: 100px;
    font-size: 13px;
  }
}

.navbar-styled {
  --navbar-accent: var(--accent, #ed7b32);
  --navbar-text: var(--nav-text, #f5f7f4);
  --navbar-surface: var(--nav-background, rgba(66, 110, 119, 0.86));
  --navbar-border: var(--glass-border, rgba(220, 235, 232, 0.18));
  --navbar-strong-border: var(--glass-border-strong, rgba(220, 235, 232, 0.28));
  --navbar-control: var(--control-background, rgba(7, 21, 37, 0.42));
  --navbar-control-border: var(--control-border, rgba(220, 235, 232, 0.2));
  --navbar-menu: var(--menu-background, rgba(7, 21, 37, 0.97));
  color: var(--navbar-text);
}

.landing-nav {
  position: absolute;
  top: 14px;
  left: 50%;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  width: max-content;
  max-width: calc(100% - 32px);
  min-height: 62px;
  padding: 7px 10px 7px 18px;
  border: 1px solid var(--navbar-border);
  border-radius: 21px;
  background: var(--navbar-surface);
  box-shadow: 0 12px 35px var(--glass-shadow, rgba(0, 0, 0, 0.25));
  transform: translateX(-50%);
  backdrop-filter: blur(18px);
}

.mobile-menu-toggle {
  display: none;
  width: 42px;
  height: 42px;
  place-items: center;
  padding: 0;
  border: 1px solid var(--navbar-control-border);
  border-radius: 12px;
  color: var(--navbar-text);
  background: var(--navbar-control);
  cursor: pointer;
}

.mobile-menu-toggle:hover,
.mobile-menu-close:hover { border-color: var(--navbar-accent); color: var(--navbar-accent); }

.menu-icon { display: grid; gap: 4px; width: 18px; }
.menu-icon i { display: block; height: 2px; border-radius: 2px; background: currentColor; }

.nav-brand {
  display: inline-flex;
  width: 66px;
  height: 46px;
  flex: 0 0 66px;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: transform 0.2s ease;
}

.brand-cluster {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.nav-brand:hover { transform: translateY(-2px); }
.nav-brand img { display: block; width: 100%; height: 100%; object-fit: contain; }

.nav-icon-button,
.theme-toggle {
  display: inline-grid;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  place-items: center;
  padding: 0;
  border: 1px solid var(--navbar-control-border);
  border-radius: 12px;
  color: var(--navbar-text);
  background: var(--navbar-control);
  cursor: pointer;
}

.nav-icon-button:hover,
.theme-toggle:hover { border-color: var(--navbar-accent); transform: translateY(-2px); }

.nav-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  color: var(--navbar-text);
  font-size: 13px;
  white-space: nowrap;
}

.nav-links a,
.auth-link { color: inherit; text-decoration: none; transition: color 0.2s ease; }
.nav-links a:hover,
.auth-link:hover { color: var(--navbar-accent); }

.auth-actions,
.nav-controls,
.user-trigger,
.language-trigger { display: flex; align-items: center; }

.auth-actions { gap: 6px; white-space: nowrap; }
.auth-link { padding: 9px 11px; border-radius: 10px; font-size: 12px; font-weight: 700; }
.auth-register { color: #fff; background: var(--navbar-accent); }

.nav-controls { gap: 7px; }

.language-picker,
.user-menu { position: relative; }

.language-trigger,
.user-trigger {
  gap: 7px;
  min-height: 42px;
  padding: 0 9px;
  border: 1px solid var(--navbar-control-border);
  border-radius: 13px;
  color: var(--navbar-text);
  background: var(--navbar-control);
  cursor: pointer;
}

.language-trigger:hover,
.user-trigger:hover,
.language-picker.is-open .language-trigger { border-color: var(--navbar-accent); }
.language-name { min-width: 54px; text-align: start; white-space: nowrap; font-size: 12px; }
.language-flag { font-size: 16px; line-height: 1; }
.language-chevron { width: 15px; height: 15px; object-fit: contain; opacity: 0.8; filter: brightness(0) invert(1); }
.language-picker.is-open .language-chevron { transform: rotate(180deg); }

.language-menu,
.user-dropdown {
  position: absolute;
  top: calc(100% + 9px);
  right: 0;
  z-index: 30;
  display: grid;
  min-width: 180px;
  gap: 4px;
  padding: 8px;
  border: 1px solid var(--navbar-control-border);
  border-radius: 15px;
  background: var(--navbar-menu);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.24);
}

.language-option,
.user-dropdown button {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  min-height: 38px;
  padding: 6px 9px;
  border: 0;
  border-radius: 10px;
  color: var(--navbar-text);
  background: transparent;
  cursor: pointer;
  text-align: start;
}

.language-option:hover,
.user-dropdown button:hover { color: var(--navbar-accent); background: rgba(62, 107, 181, 0.16); }

.user-trigger { min-width: 42px; }
.user-avatar,
.profile-avatar {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: #fff;
  background: var(--navbar-accent);
  font-weight: 800;
}
.user-name { max-width: 96px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; }
.user-chevron { font-size: 16px; }
.user-dropdown { min-width: 190px; }
.user-dropdown strong { padding: 5px 9px; color: var(--navbar-text); font-size: 12px; }
.dropdown-divider { height: 1px; margin: 2px 0; background: var(--navbar-control-border); }
.user-dropdown .danger-action { color: #ff8f8f; }

.company-bar {
  position: absolute;
  top: 86px;
  left: 50%;
  z-index: 9;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100% - 32px);
  padding: 7px 12px;
  border: 1px solid var(--navbar-border);
  border-radius: 13px;
  color: var(--navbar-text);
  background: var(--navbar-control);
  transform: translateX(-50%);
  backdrop-filter: blur(14px);
  font-size: 11px;
}

.mobile-menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(2, 10, 18, 0.54);
  backdrop-filter: blur(3px);
}

.mobile-menu {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
  display: flex;
  width: min(320px, 86vw);
  flex-direction: column;
  gap: 18px;
  padding: 20px 18px;
  overflow-y: auto;
  border-left: 1px solid var(--navbar-border);
  color: var(--navbar-text);
  background: var(--navbar-menu);
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.28);
  transform: translateX(100%);
  transition: transform 0.25s ease;
}

.mobile-menu.is-open { transform: translateX(0); }

.mobile-menu-header,
.mobile-user-card { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.mobile-menu-header { padding-bottom: 14px; border-bottom: 1px solid var(--navbar-control-border); }
.mobile-menu-header img { width: 78px; height: 48px; object-fit: contain; }
.mobile-menu-close { display: grid; width: 38px; height: 38px; place-items: center; border: 1px solid var(--navbar-control-border); border-radius: 10px; color: var(--navbar-text); background: transparent; cursor: pointer; font-size: 24px; }
.mobile-user-card { justify-content: flex-start; padding: 12px; border: 1px solid var(--navbar-control-border); border-radius: 14px; background: var(--navbar-control); }
.mobile-user-card strong,
.mobile-user-card small { display: block; }
.mobile-user-card strong { font-size: 13px; }
.mobile-user-card small { margin-top: 3px; color: var(--text-muted, #b7c3c2); font-size: 11px; }

.mobile-menu-links { display: grid; gap: 8px; }
.mobile-menu-links a,
.mobile-menu-links button { display: flex; min-height: 44px; align-items: center; width: 100%; padding: 0 13px; border: 1px solid transparent; border-radius: 11px; color: var(--navbar-text); background: transparent; font: inherit; font-size: 13px; text-align: start; text-decoration: none; cursor: pointer; }
.mobile-menu-links a:hover,
.mobile-menu-links button:hover { border-color: var(--navbar-control-border); color: var(--navbar-accent); background: var(--navbar-control); }
.mobile-menu-links .mobile-register-link { justify-content: center; color: #fff; background: var(--navbar-accent); }
.mobile-menu-links .mobile-danger-link { color: #ff8f8f; }
.mobile-role-switcher { padding-top: 4px; border-top: 1px solid var(--navbar-control-border); }

.company-name { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.database-switcher select,
.active-database {
  min-height: 30px;
  padding: 0 8px;
  border: 1px solid var(--navbar-control-border);
  border-radius: 8px;
  color: var(--navbar-text);
  background: transparent;
  font: inherit;
}

.profile-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(2, 10, 18, 0.7);
  backdrop-filter: blur(8px);
}

.profile-modal {
  position: relative;
  width: min(460px, 100%);
  display: grid;
  gap: 14px;
  padding: 24px;
  border: 1px solid var(--navbar-border);
  border-radius: 22px;
  color: var(--navbar-text);
  background: var(--navbar-menu);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.36);
}

.profile-modal-header,
.profile-modal-actions { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.profile-modal-header h2 { margin: 0; font-size: 20px; }
.modal-close { border: 0; color: var(--navbar-text); background: transparent; cursor: pointer; font-size: 24px; }
.profile-avatar { width: 54px; height: 54px; margin-bottom: 2px; font-size: 20px; }
.profile-modal label { display: grid; gap: 6px; font-size: 11px; }
.profile-modal input { min-height: 40px; padding: 0 11px; border: 1px solid var(--navbar-control-border); border-radius: 10px; color: var(--navbar-text); background: var(--navbar-control); }
.profile-modal input:disabled { opacity: 0.65; }
.button { display: inline-flex; min-height: 40px; align-items: center; justify-content: center; padding: 0 14px; border: 1px solid transparent; border-radius: 10px; font: inherit; font-size: 11px; font-weight: 700; cursor: pointer; }
.button-primary { color: #fff; background: var(--navbar-accent); }
.button-ghost { border-color: var(--navbar-control-border); color: var(--navbar-text); background: transparent; }
.button:disabled { cursor: not-allowed; opacity: 0.5; }

@media (max-width: 1040px) {
  .landing-nav { gap: 8px; padding-inline: 10px; }
  .nav-links { gap: 12px; }
  .user-name { display: none; }
}

@media (max-width: 760px) {
  .brand-cluster {
  margin: 0px 0px 0px 48px
  }
}

@media (max-width: 760px) {
  .landing-nav { top: 10px; width: calc(100% - 20px); max-width: none; min-height: 58px; justify-content: center; padding: 7px 58px; }
  .mobile-menu-toggle { position: absolute; top: 8px; right: 9px; display: grid; }
  .nav-brand { width: 62px; height: 42px; flex-basis: 62px; }
  .nav-links,
  .auth-actions,
  .role-switcher-wrap,
  .user-menu { display: none; }
  .nav-controls { position: absolute; top: 8px; left: 9px; }
  .nav-icon-button { position: absolute; top: 8px; left: 9px; }
  .nav-icon-button + .nav-brand { margin-left: 42px; }
  .language-name { display: none; }
  .language-trigger { padding-inline: 8px; }
  .language-menu {
    right: auto;
    left: 0;
    min-width: 180px;
    max-width: calc(100vw - 24px);
  }
  .company-bar { top: 116px; max-width: calc(100% - 24px); }
}
</style>
